import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Arrow,
  Content,
  Portal,
  Provider as TooltipProvider,
  Root,
  Trigger,
} from '@radix-ui/react-tooltip';
import { Image } from '@sitecore-jss/sitecore-jss-nextjs';

import { icon_plus as IconPlus } from '@gn-audio/icons';
import { getTailwindColor } from '@gn-audio/jss-components/utils';

import { useBreakpoint } from 'utils/hooks/useBreakpoint';
import useFallbackTranslation from 'utils/hooks/useFallbackTranslation';

import TooltipButton from './TooltipButton';

export type Spot<Data> = {
  id: string;
  x: `${string}%`;
  y: `${string}%`;
  data?: Data;
  renderItem?: (
    spotData: Data & { onClose: () => void; position?: 'top' | 'bottom' }
  ) => React.ReactNode;
};

export interface ImageSpotsProps<Data> {
  config: {
    isLightBackground?: boolean;
    backgroundCircleColor?: string;
    maxHeight?: string;
    image?: {
      alt?: string;
      src?: string;
    };
  };
  spots?: Spot<Data>[];
}

const ImageSpots = <Data extends {}>({ spots, config }: ImageSpotsProps<Data>) => {
  const { t } = useFallbackTranslation();

  const { isAboveMd } = useBreakpoint('md', true);
  const [openId, setOpenId] = useState<string | undefined>();
  const [activeButtonId, setActiveButtonId] = useState<string | undefined>();
  const triggerRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const contentRef = useRef<HTMLDivElement>(null);

  const bgColor = config.backgroundCircleColor?.startsWith('#')
    ? config.backgroundCircleColor
    : getTailwindColor(config.backgroundCircleColor || 'white');

  const handleOpenChange = useCallback(
    (id: string, shouldOpen: boolean) => {
      if (shouldOpen) {
        if (activeButtonId !== id) {
          setOpenId(id);
          setActiveButtonId(undefined);
        }
      } else if (!activeButtonId || activeButtonId !== id) {
        setOpenId(undefined);
      }
    },
    [activeButtonId]
  );

  const closeTooltip = useCallback(() => {
    setOpenId(undefined);
    setActiveButtonId(undefined);
  }, []);

  // Handle button clicks
  const handleClick = useCallback(
    (id: string) => {
      if (activeButtonId === id) {
        closeTooltip();
      } else {
        setActiveButtonId(id);
        setOpenId(id);
      }
    },
    [activeButtonId, closeTooltip]
  );

  const handlePointerDownOutside = useCallback(
    (event: Event) => {
      const trigger = triggerRefs.current.get(openId!);
      if (
        (trigger && trigger.contains(event.target as Node)) ||
        (contentRef.current && contentRef.current.contains(event.target as Node))
      ) {
        return;
      }
      closeTooltip();
    },
    [openId, closeTooltip]
  );

  useEffect(() => {
    if (!openId) return;
    const listener = (event: Event) => handlePointerDownOutside(event);
    document.addEventListener('mousedown', listener);
    return () => document.removeEventListener('mousedown', listener);
  }, [openId, handlePointerDownOutside]);

  return (
    <TooltipProvider>
      <div
        className={`rounded-full ${bgColor ? 'p-4 md:p-6' : 'p-0'} ${
          config.isLightBackground ? 'light' : 'dark'
        }`}
        style={{ backgroundColor: bgColor }}
      >
        <div className="relative inline-block w-fit">
          {spots?.map((spot) => {
            const isOpen = openId === spot.id;

            return (
              <Root delayDuration={0} key={`${spot.id}-${isOpen}`} open={isOpen}>
                <Trigger asChild>
                  <TooltipButton
                    ref={(el) => {
                      if (el) triggerRefs.current.set(spot.id, el);
                    }}
                    style={{ top: spot.y, left: spot.x }}
                    className="absolute"
                    size={isAboveMd ? 'l' : 's'}
                    variant="secondary"
                    onMouseEnter={() => handleOpenChange(spot.id, true)}
                    onMouseLeave={() => handleOpenChange(spot.id, false)}
                    onClick={(event) => {
                      event.stopPropagation();
                      handleClick(spot.id); // Handle button click
                    }}
                    aria-label={config.image?.alt || t('system.TooltipButton')}
                    icon={<IconPlus viewBox="0 0 24 24" aria-hidden={true}></IconPlus>}
                    active={spot.id === activeButtonId}
                  />
                </Trigger>
                <Portal>
                  <Content
                    id={`tooltip-content-${spot.id}`}
                    avoidCollisions={true}
                    sideOffset={isAboveMd ? 4 : undefined}
                    align="center"
                    role="tooltip"
                    ref={contentRef}
                  >
                    {spot.data && spot.renderItem
                      ? spot?.renderItem({
                          ...spot.data,
                          onClose: closeTooltip,
                        })
                      : null}
                    <Arrow className="h-3 w-4 fill-white" />
                  </Content>
                </Portal>
              </Root>
            );
          })}
          <Image
            className="m-auto"
            style={{ maxHeight: `${config.maxHeight || '768px'}` }}
            alt={config.image?.alt}
            field={{ src: config?.image?.src }}
          />
        </div>
      </div>
    </TooltipProvider>
  );
};

export default ImageSpots;
